import { flatten } from 'lodash';
import * as moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import { Button, OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import requiredIf from 'react-required-if';
import { Link } from 'react-router-dom';

import MetricsTableHeadersContainer from './../Containers/MetricTableHeadersContainer';
import {
  job as jobConstants,
  metrics as metricHelpers,
  unicode
} from './../helpers';

const getMetricsLink = (metric) => {
  // strip the image's file extension
  const image = encodeURIComponent(metric.image.slice(0, metric.image.lastIndexOf('.')))
    .toLowerCase();

  let startDate = new Date(new Date().setDate(new Date().getDate() - 7)).getTime();

  // Move back to the created date if necessary
  startDate = metric.createdDate && startDate > metric.createdDate.getTime() ?
    metric.createdDate.getTime() :
    startDate;

  // Move back to the baseline created date if necessary
  startDate = metric.baselineCreatedDate &&
    startDate > metric.baselineCreatedDate.getTime() ?
    metric.baselineCreatedDate.getTime() :
    startDate;

  // Convert it to a timestamp
  startDate = moment.unix((startDate / 1000).toFixed(0)).startOf('day').unix();

  return image.startsWith('er') ?
    `/engine-metrics?c=60&i=${image}&n=all&ts=${startDate}&` +
    `ji=${metric.jobId}&` +
    `jn=${jobConstants.getJobNumber(metric.job)}` :
    `/case-metrics?c=60&i=${image}&n=all&ts=${startDate}&` +
    `ji=${metric.jobId}&` +
    `jn=${jobConstants.getJobNumber(metric.job)}`;
};

const getMcatMetricClassName = (mcatDiffResult) => {
  let className = '';

  if (mcatDiffResult === 'pass') {
    className += 'good-metric';
  }
  else if (mcatDiffResult === 'fail') {
    className += 'bad-metric';
  }

  return className;
};

const getDurationMetricClassName = (durationPercent) => {
  if (!durationPercent) {
    return undefined;
  }

  const percent = parseInt(durationPercent);
  if (isNaN(percent)) {
    return 'bad-metric';
  }

  if (percent > 5) {
    return 'bad-metric';
  }

  if (percent > 0) {
    return 'warn-metric';
  }

  return undefined;
};

const getHitCountMetricClassName = (hitCountDiff) => {
  if (isNaN(hitCountDiff) || hitCountDiff !== 0) {
    return 'bad-metric';
  }

  return undefined;
};

const getValueString = (value, unit = '', showSign = false) => {
  if (isNaN(value) &&
    value !== `+${unicode.INFINITY}` &&
    value !== `-${unicode.INFINITY}` &&
    value !== 'pass' &&
    value !== 'fail') {
    return 'N/A';
  }

  if (value === 'pass') {
    return unicode.CHECK_MARK;
  }

  if (value === 'fail') {
    return unicode.RED_CROSS_MARK;
  }

  let valueString = value + unit;
  if (showSign) {
    valueString = (value > 0 ? '+' : '') + valueString;
  }

  return valueString;
};

const buildDurationMetricColumns = (metric, type) => {
  const durationMetrics = metric[type].durationMetrics;
  const className = getDurationMetricClassName(durationMetrics.percent);
  return [
    <td key={`${type}-current-duration`}>
      {getValueString(durationMetrics.current)}
    </td>,
    <td key={`${type}-baseline-duration`}>
      {getValueString(durationMetrics.baseline)}
    </td>,
    <td key={`${type}-percent-duration`} className={className}>
      {getValueString(durationMetrics.percent, '%', true)}
    </td>,
    <td key={`${type}-diff-duration`} className={`${className} divider-right`}>
      {getValueString(durationMetrics.diff, '', true)}
    </td>
  ];
};

const buildHitCountMetricColumns = (metric, type, i, onHitCountClick, art, delta) => {
  const hitCountMetrics = metric[type].hitCountMetrics;

  return [
    <td key={`${type}-current-hitCount`}>
      {
        <a
          id={'hit-count-' + i}
          onClick={() => onHitCountClick(metric, type === 'ccf')}
        >
          {art ? getValueString(hitCountMetrics.baseline + delta) :
            getValueString(hitCountMetrics.current)}
        </a>
      }
    </td>,
    <td key={`${type}-baseline-hitCount`}>
      {getValueString(hitCountMetrics.baseline)}
    </td>,
    <td
      key={`${type}-diff-hitCount`}
      className={getHitCountMetricClassName(hitCountMetrics.diff)}
    >
      { delta ? getValueString(delta, '', true) :
        getValueString(hitCountMetrics.diff, '', true) }
    </td>,
    <td
      key={`${type}-diff-hitCountResult`}
      style={hitCountMetrics.diffResult === 'pass' ? { color : 'green' } : {}}
    >
      {getValueString(hitCountMetrics.diffResult)}
    </td>,
    mcatMetricColumns(metric, type)
  ];
};

const buildSourceCountMetricColumns = (metric, type) => {
  const sourceCountMetrics = metric[type].sourceCountMetrics;

  return [
    <td
      key={`${type}-current-sourceCount`}
      style={type !== 'ncf' ? { border : 'none' } : {}}
    >
      {type === 'ncf' ? getValueString(sourceCountMetrics.current) : ''}
    </td>,
    <td
      key={`${type}-baseline-sourceCount`}
      style={type !== 'ncf' ? { border : 'none' } : {}}
    >
      {type === 'ncf' ? getValueString(sourceCountMetrics.baseline) : ''}
    </td>,
    <td
      key={`${type}-diff-sourceCount`}
      className={getHitCountMetricClassName(sourceCountMetrics.diff)}
      style={type !== 'ncf' ? { border: 'none' } : {}}
    >
      {type === 'ncf' ? getValueString(sourceCountMetrics.diff, '', true) : ''}
    </td>,
    <td
      key={`${type}-diff-sourceCountResult`}
      className={'divider-right'}
      style={type === 'ncf' && sourceCountMetrics.diffResult === 'pass' ? {
        borderLeft : 'none',
        color : 'green'
      } : { borderLeft : 'none' }}
    >
      {type === 'ncf' ? getValueString(sourceCountMetrics.diffResult) : ''}
    </td>
  ];
};

const buildSourceAttributeCountMetricColumns = (metric, type) => {
  const sourceAttributeCountMetrics = metric[type].sourceAttributeCountMetrics;
  const resultClassName = getHitCountMetricClassName(sourceAttributeCountMetrics.diff);
  return [
    <td
      key={`${type}-currentSourceAttributeCount`}
      style={type !== 'ncf' ? { border : 'none' } : {}}
    >
      {type === 'ncf' ? getValueString(sourceAttributeCountMetrics.current) : ''}
    </td>,
    <td
      key={`${type}-baseline-SourceAttributeCount`}
      style={type !== 'ncf' ? { border : 'none' } : {}}
    >
      {type === 'ncf' ? getValueString(sourceAttributeCountMetrics.baseline) : ''}
    </td>,
    <td
      key={`${type}-diff-sourceAttributeCount`}
      className={`${resultClassName} divider-right`}
      style={type !== 'ncf' ?
        { borderLeft : 'none' } :
        {}
      }
    >
      {type === 'ncf' ? getValueString(sourceAttributeCountMetrics.diff, '', true) : ''}
    </td>
  ];
};

const mcatMetricColumns = (metric, type) => {
  // values only expected for ncf
  const mcatDiffResult = metric[type].hitCountMetrics[metricHelpers.NCF_MCAT_DIFFRESULT];
  const displayValue = mcatDiffResult ?
    unicode.generalResultIconLookup[mcatDiffResult] :
    'N/A';
  const className = getMcatMetricClassName(mcatDiffResult);
  return [
    <td
      key={`${type}-mcat-column`}
      className={`${className} divider-right`}
    >
      {displayValue}
    </td>
  ];
};

const isExpectedButtonDisabled = (metric, ccfNcfBuildOrder) => {
  const check = (hitCountDiff) =>
    hitCountDiff === '0';

  const mainHitCountDiff = metric[ccfNcfBuildOrder.main].hitCountMetrics.diff;
  const otherHitCountDiff = ccfNcfBuildOrder.other ?
    metric[ccfNcfBuildOrder.other].hitCountMetrics.diff :
    0;
  const sourceCountDiff =
    metric[ccfNcfBuildOrder.main].sourceCountMetrics &&
    metric[ccfNcfBuildOrder.main].sourceCountMetrics.diff || NaN;
  const sourceAttributeCountDiff =
    metric[ccfNcfBuildOrder.main].sourceAttributeCountMetrics &&
    metric[ccfNcfBuildOrder.main].sourceAttributeCountMetrics.diff || NaN;

  return check(mainHitCountDiff) && check(otherHitCountDiff) &&
    check(sourceCountDiff) && check(sourceAttributeCountDiff);
};

const buildNumberColumns = (baselineBuildNumberByImage, metric, type) => {
  const displayValue =
    !baselineBuildNumberByImage || !baselineBuildNumberByImage[metric.image] ?
      'N/A' :
      baselineBuildNumberByImage[metric.image][type];

  return [
    <td
      key={'build-number-column'}
      className={'divider-right'}
    >
      {displayValue}
    </td>
  ];
};

const buildCcfNcfColumns = (ccfNcfBuildOrder) => [
  <td
    key={'ccf-ncf-column'}
    className={'divider-right'}
  >
    <b>{ccfNcfBuildOrder.toUpperCase()}</b>
  </td>
];

const columns = (
  metric,
  ccfNcfBuildOrder,
  i,
  onHitCountClick,
  hideDuration,
  showSources,
  baselineBuildNumberByImage,
  art,
  delta) => flatten([
  buildCcfNcfColumns(ccfNcfBuildOrder),
  !hideDuration ? buildDurationMetricColumns(metric, ccfNcfBuildOrder) : [],
  buildHitCountMetricColumns(metric, ccfNcfBuildOrder, i, onHitCountClick, art, delta),
  showSources ?
    buildSourceCountMetricColumns(metric, ccfNcfBuildOrder) : [],
  showSources ?
    buildSourceAttributeCountMetricColumns(metric, ccfNcfBuildOrder) : [],
  baselineBuildNumberByImage ?
    buildNumberColumns(baselineBuildNumberByImage, metric, ccfNcfBuildOrder) : []
]);

const getImageName = (metric) => metric.image.replace(/_/g, ' ');

const MetricsTableComponent = ({
  metrics,
  highlightBar,
  onHitCountClick,
  isExpectedBaseline,
  openConfirm,
  baselineBuildNumberByImage,
  ccfNcfBuildOrder,
  hideDuration,
  disableImageLink,
  showSources,
  art,
  delta
}) => (
  <Table bordered condensed
    className={disableImageLink ? 'metrics-table-jira' : 'metrics-table-normal'}
  >
    <MetricsTableHeadersContainer
      hideDuration={hideDuration}
      hideBuildNumbers={baselineBuildNumberByImage === undefined}
      showSources={showSources}
    />
    {
      metrics.map((t, i) => {
        const tooltip = (
          <Tooltip id={'image-name-tooltip-' + i} style={{ width: 'auto' }} >
            {getImageName(t)}
          </Tooltip>
        );
        return (
          <tbody key={'metric-tbody-' + i}>
            <tr
              id={'metric-ncf-row-' + i}
              key={'metric-ncf-row-' + i}
              style={t.latest ? undefined : { textDecoration: 'line-through' }}
              className={`${t.latest ? '' : 'text-muted'}${t.highlight ? 'active' : ''}`}
              onClick={
                () => highlightBar ? highlightBar({ _model: { label: t.image }}) : {}
              }
            >
              <td
                style=
                  {{
                    maxWidth: '250px',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    verticalAlign: 'middle'
                  }}
                rowSpan={ccfNcfBuildOrder.other ? '2' : '1'}
              >
                {
                  !disableImageLink ?
                    <OverlayTrigger key={'metric-overlay-' + i} overlay={tooltip}
                      placement="left" delayShow={250}
                    >
                      <Link id={'metric-link-' + i} to={getMetricsLink(t)}>
                        {getImageName(t)}
                      </Link>
                    </OverlayTrigger> :
                    getImageName(t)
                }
              </td>
              {
                columns(t, ccfNcfBuildOrder.main, i, onHitCountClick,
                  hideDuration, showSources, baselineBuildNumberByImage, art, delta)
              }
              {
                isExpectedBaseline && ccfNcfBuildOrder.main === 'ncf' ?
                  <td
                    rowSpan={ccfNcfBuildOrder.other ? 2 : 1}
                    style={{ verticalAlign : 'middle' }}
                    align="center"
                  >
                    <Button
                      id={'expected-button-' + i}
                      onClick={() => openConfirm ?
                        openConfirm(t, ccfNcfBuildOrder, showSources) : {}}
                      disabled={isExpectedButtonDisabled(t, ccfNcfBuildOrder)}
                    >
                      Expect
                    </Button>
                  </td> : null
              }
            </tr>
            {
              ccfNcfBuildOrder.other ?
                <tr id={'metric-ccf-row-' + i}
                  key={'metric-ccf-row-' + i}
                  style={t.latest ? undefined : { textDecoration: 'line-through' }}
                  className={`${t.latest ? '' : 'text-muted'}
                    ${t.highlight ? 'active' : ''}`}
                  onClick={
                    () => highlightBar ? highlightBar({ _model: { label: t.image }}) : {}}
                >
                  {
                    ccfNcfBuildOrder.other ?
                      columns(t, ccfNcfBuildOrder.other, i, onHitCountClick,
                        hideDuration, showSources, baselineBuildNumberByImage,
                        art, delta) : null
                  }
                </tr> : null
            }
          </tbody>
        );
      })
    }
    {metrics.length === 0 && <tbody><tr><td>{'No runs to show...'}</td></tr></tbody>}
  </Table>
);

MetricsTableComponent.propTypes = {
  metrics: PropTypes.array.isRequired,
  highlightBar: PropTypes.func,
  clickableDiff: PropTypes.bool,
  onHitCountClick: requiredIf(PropTypes.func,
    props => props.showSources && props.clickableDiff),
  isExpectedBaseline: PropTypes.bool,
  openConfirm: PropTypes.func,
  baselineBuildNumberByImage: PropTypes.object,
  ccfNcfBuildOrder: PropTypes.shape({
    main: PropTypes.string.isRequired,
    other: PropTypes.string
  }).isRequired,
  hideDuration: PropTypes.bool,
  disableImageLink: PropTypes.bool,
  showSources: PropTypes.bool,
  art: PropTypes.string,
  delta: PropTypes.number
};

export default MetricsTableComponent;
export const testingExports = {
  getDurationMetricClassName,
  getHitCountMetricClassName,
  getMcatMetricClassName
};


// WEBPACK FOOTER //
// src/Components/MetricsTableComponent.jsx